import { fetchChurches, fetchChurchById } from '../api/churchApi';
import { Church, ChurchQueryParams, ChurchListResponse, ChurchMeta } from '../types/church';
import ApiResponse from '@core/types/apiResponse';
import { createListService, makeServiceErrorResponse } from '@core/services/createListService';
import { formatDateOnly } from '@core/utils/formatDateOnly';

/**
 * Service layer for church-related business logic and data processing.
 * The list flow (validate / fetch / process) is assembled with the shared
 * `createListService` factory and consumed by `usePaginatedList`.
 */

/**
 * Validates church query parameters
 * @param params - Query parameters to validate
 * @returns Validation result with processed parameters
 */
export const validateChurchQueryParams = (params: ChurchQueryParams): {
  isValid: boolean;
  processedParams: ChurchQueryParams;
  errors: string[]
} => {
  const errors: string[] = [];
  const processedParams = { ...params };

  // Validate pagination
  if (params.perPage && (params.perPage < 1 || params.perPage > 100)) {
    errors.push('Per page must be between 1 and 100');
    processedParams.perPage = Math.max(1, Math.min(100, params.perPage || 10));
  }

  if (params.page && params.page < 1) {
    errors.push('Page must be greater than 0');
    processedParams.page = 1;
  }

  // Validate search parameters
  if (params.churchName && params.churchName.length < 2) {
    errors.push('Church name search must be at least 2 characters');
  }

  if (params.districtId && params.districtId <= 0) {
    errors.push('Invalid district ID');
  }

  return {
    isValid: errors.length === 0,
    processedParams,
    errors
  };
};

/** Adds computed display fields to a church row. */
const mapChurchForDisplay = (church: Church): Church => ({
  ...church,
  displayName: church.church_name,
  isActive: church.status === 'active',
  memberCountDisplay: church.member_count ? `${church.member_count} members` : 'No data',
  establishedDateDisplay: formatDateOnly(church.established_date) ?? 'Not specified',
} as Church);

export const churchListService = createListService<Church, ChurchQueryParams, ChurchMeta, ChurchListResponse>({
  fetchPage: fetchChurches,
  validateParams: validateChurchQueryParams,
  mapRow: mapChurchForDisplay,
  filterKeys: ['freetext', 'churchName', 'districtName'],
  errorTitle: 'Church Service Error',
});

/**
 * Fetches churches with business logic for parameter processing and error handling
 * @param params - Query parameters for filtering and pagination
 */
export const fetchChurchesAsync = churchListService.fetchAsync;

/**
 * Processes church data for display (business logic)
 * @param churches - Array of church data
 */
export const processChurchesForDisplay = churchListService.processForDisplay;

/**
 * Fetches a single church by ID with business logic
 * @param id - Church ID
 * @returns Promise<ApiResponse<Church>>
 */
export const fetchChurchByIdAsync = async (id: number): Promise<ApiResponse<Church>> => {
  try {
    if (!id || id <= 0) {
      throw new Error('Invalid church ID provided');
    }
    return await fetchChurchById(id);
  } catch (error) {
    return makeServiceErrorResponse<Church>(error, 'Church Service Error');
  }
};
